/**
 * Browser-safe exports for plugin web manifests.
 *
 * This subpath provides the helpers and types needed to define plugin web
 * manifests without pulling in server-side dependencies. Plugin
 * `web-manifest.ts` files should import from `@reflex/plugin-api/web`
 * instead of the root `@reflex/plugin-api`.
 *
 * @module @reflex/plugin-api/web
 */

// --- Web manifest types ---

export type {
  AuthProviderWebConfig,
  PluginIconComponent,
  PluginWebRegistration,
  PluginWebRoute,
  PluginMarketingRouteMeta,
  PluginDocsRouteMeta,
  PluginNavItem,
  PluginCommand,
  PluginOverviewCard,
  PluginAgentAction,
  PluginAdminUserAction,
  AdminUserActionProps,
  PluginAgentTab,
  AgentTabProps,
  PluginPageTab,
  PluginSettingsSection,
  PluginProfileSection,
  PluginAgentDetailSection,
  AgentDetailSectionProps,
  PluginAgentHeaderBadge,
  AgentHeaderBadgeProps,
  PluginAssociationChip,
  AssociationChipProps,
  PluginHomeWidget,
  HomeWidgetProps,
  PluginEventLabel,
  PluginAttachmentDefinition,
  AttachmentSummaryProps,
  PluginAttachmentPickerProps,
  AttachmentPickerProviderInfo,
  PluginSearchProvider,
  SearchProviderProps,
  SearchResultAction,
  PluginLaunchMentionProvider,
  LaunchMentionProviderProps,
  PluginChatMentionProvider,
  ChatMentionProviderProps,
  LaunchMentionCreateProps,
  LaunchMentionResult,
  PluginAgentContextProvider,
  AgentContextProviderProps,
  PluginFlowTriggerRenderer,
  FlowTriggerRendererProps,
  PluginToolCallRenderer,
  ToolCallRendererProps,
  PluginResourcePreview,
  ResourcePreviewProps,
  PluginUserSetupStep,
  PluginUserSetupStepKind,
  PluginMarketingPreview,
  PluginMarketingPreviewSurface,
  PluginAdminSetupComponent,
  PluginOrgSetupComponent,
  PluginGlobalUiElement,
  PluginNavUtilityElement,
  PluginNavUtilityProps,
  PluginHomePage,
  PluginWebManifest,
} from './types/web.js';

import type {
  PluginWebRoute,
  PluginNavItem,
  PluginCommand,
  PluginOverviewCard,
  PluginWebManifest,
  PluginAgentAction,
  PluginAgentTab,
  PluginPageTab,
  PluginSettingsSection,
  PluginProfileSection,
  PluginAgentDetailSection,
  PluginAgentHeaderBadge,
  PluginAssociationChip,
  PluginHomeWidget,
  PluginAttachmentDefinition,
  PluginEventLabel,
  PluginSearchProvider,
  PluginFlowTriggerRenderer,
  PluginResourcePreview,
  PluginMarketingPreview,
} from './types/web.js';

// --- definePage helper ---

type NavGroup = PluginNavItem['group'];
type CommandGroup = PluginCommand['group'];

const NAV_TO_COMMAND_GROUP: Record<NavGroup, CommandGroup> = {
  main: 'navigation',
  integrations: 'integrations',
  misc: 'misc',
  plugins: 'misc',
  security: 'misc',
  admin: 'admin',
  bottom: 'misc',
  organization: 'navigation',
  'user-menu': 'settings',
  'takeover-footer': 'misc',
};

interface PageDefinition {
  path: string;
  label: string;
  icon: string;
  group: NavGroup;
  componentPath: string;
  config?: Record<string, unknown>;
  description?: string;
  details?: string;
  features?: string[];
  overviewGroup?: PluginOverviewCard['group'];
  /**
   * When true, the route mounts at the top level of the SPA (not
   * under `/orgs/:orgSlug/*`) and the nav/command links omit the org slug.
   * Use for cross-organization pages like the changelog.
   */
  rootLevel?: boolean;
  /**
   * Optional maturity stage forwarded to the nav item. When set, the
   * sidebar renders a subtle "Alpha" or "Beta" marker after the label.
   */
  maturityStage?: PluginNavItem['maturityStage'];
  /**
   * When true, the page opts into the Labs takeover's fullscreen mode
   * (see {@link PluginWebRoute.fullscreen}). Forwarded to the route only.
   */
  fullscreen?: boolean;
}

interface PageContributions {
  route: PluginWebRoute;
  navItem: PluginNavItem;
  command: PluginCommand;
  overviewCard?: PluginOverviewCard;
  web: Pick<PluginWebManifest, 'routes' | 'navItems' | 'commands' | 'overviewCards'>;
}

/**
 * Generates a route, nav item, command, and optional overview card from a
 * single page declaration. Eliminates the repetition of `path`, `label`,
 * `icon`, and `group` across three separate manifest arrays.
 *
 * @example
 * const page = definePage({
 *   path: '/costs',
 *   label: 'Costs',
 *   icon: 'DollarSign',
 *   group: 'misc',
 *   componentPath: '@reflex/plugin-cost/web/CostPage',
 *   description: 'LLM spend tracking.',
 *   details: 'Track token usage...',
 *   features: ['Per-model costs', 'Usage trends'],
 * });
 *
 * // Spread into the web manifest:
 * web: { ...page.web, agentDetailSections: [...] }
 */
export function definePage(def: PageDefinition): PageContributions {
  const route: PluginWebRoute = {
    path: def.path,
    componentPath: def.componentPath,
    ...(def.config ? { config: def.config } : {}),
    ...(def.rootLevel ? { rootLevel: true } : {}),
    ...(def.fullscreen ? { fullscreen: true } : {}),
  };
  const navItem: PluginNavItem = {
    to: def.path,
    label: def.label,
    icon: def.icon,
    group: def.group,
    ...(def.rootLevel ? { rootLevel: true } : {}),
    ...(def.maturityStage ? { maturityStage: def.maturityStage } : {}),
  };
  const command: PluginCommand = {
    label: def.label,
    to: def.path,
    icon: def.icon,
    group: NAV_TO_COMMAND_GROUP[def.group],
    ...(def.rootLevel ? { rootLevel: true } : {}),
  };
  const overviewGroup =
    def.overviewGroup ??
    (def.group === 'main' ||
    def.group === 'integrations' ||
    def.group === 'misc' ||
    def.group === 'plugins' ||
    def.group === 'security' ||
    def.group === 'organization'
      ? def.group
      : 'misc');
  const overviewCard: PluginOverviewCard | undefined =
    def.description && def.details && def.features
      ? {
          to: def.path,
          label: def.label,
          icon: def.icon,
          description: def.description,
          details: def.details,
          features: def.features,
          group: overviewGroup,
        }
      : undefined;

  return {
    route,
    navItem,
    command,
    overviewCard,
    web: {
      routes: [route],
      navItems: [navItem],
      commands: [command],
      ...(overviewCard ? { overviewCards: [overviewCard] } : {}),
    },
  };
}

// --- defineWebManifest helper ---

/**
 * A web manifest definition where `componentPath` fields are validated
 * against a known set of component keys at compile time. The full
 * `componentPath` string (`@scope/plugin-name/web/ComponentName`) is
 * derived automatically from the `pluginPrefix` and the key.
 */
interface TypedWebManifestInput<K extends string> {
  routes?: Array<{
    path: string;
    component: K;
    config?: Record<string, unknown>;
    fullscreen?: boolean;
  }>;
  navItems?: PluginNavItem[];
  commands?: PluginCommand[];
  overviewCards?: PluginOverviewCard[];
  agentActions?: Array<Omit<PluginAgentAction, 'componentPath'> & { component?: K }>;
  agentTabs?: Array<Omit<PluginAgentTab, 'componentPath'> & { component: K }>;
  pageTabs?: Array<Omit<PluginPageTab, 'componentPath'> & { component: K }>;
  settingsSections?: Array<Omit<PluginSettingsSection, 'componentPath'> & { component: K }>;
  orgSettingsSections?: Array<Omit<PluginSettingsSection, 'componentPath'> & { component: K }>;
  profileSections?: Array<Omit<PluginProfileSection, 'componentPath'> & { component: K }>;
  agentDetailSections?: Array<Omit<PluginAgentDetailSection, 'componentPath'> & { component: K }>;
  agentHeaderBadges?: Array<Omit<PluginAgentHeaderBadge, 'componentPath'> & { component: K }>;
  associationChips?: Array<Omit<PluginAssociationChip, 'componentPath'> & { component: K }>;
  homeWidgets?: Array<Omit<PluginHomeWidget, 'componentPath'> & { component: K }>;
  attachments?: Array<
    Omit<PluginAttachmentDefinition, 'pickerComponentPath' | 'summaryComponentPath'> & {
      pickerComponent: K;
      summaryComponent?: K;
    }
  >;
  eventLabels?: PluginEventLabel[];
  searchProviders?: Array<Omit<PluginSearchProvider, 'componentPath'> & { component: K }>;
  flowTriggerRenderers?: Array<Omit<PluginFlowTriggerRenderer, 'componentPath'> & { component: K }>;
  resourcePreviews?: Array<Omit<PluginResourcePreview, 'componentPath'> & { component: K }>;
  marketingPreviews?: Array<Omit<PluginMarketingPreview, 'componentPath'> & { component: K }>;
}

/**
 * Builds a `PluginWebManifest` with compile-time validation of component
 * references. Instead of writing fragile `componentPath` strings like
 * `'@reflex/plugin-cost/web/CostPage'`, you pass the component key
 * (`'CostPage'`) and the full path is derived from `pluginPrefix`.
 *
 * @param pluginPrefix - Package name prefix (e.g. `'@reflex/plugin-cost'`)
 * @param _components  - Component loader map from `register.ts` (used only for type inference)
 * @param manifest     - Typed manifest where `component` fields are keys of the components map
 *
 * @example
 * const components = {
 *   CostPage: () => import('./CostPage'),
 *   UsageSection: () => import('./UsageSection'),
 * } as const;
 *
 * web: defineWebManifest('@reflex/plugin-cost', components, {
 *   routes: [{ path: '/costs', component: 'CostPage' }],
 *   agentDetailSections: [{ id: 'usage', label: 'Usage', icon: 'BarChart3', component: 'UsageSection', order: 0 }],
 * })
 */
export function defineWebManifest<C extends Record<string, unknown>>(
  pluginPrefix: string,
  _components: C,
  manifest: TypedWebManifestInput<Extract<keyof C, string>>,
): PluginWebManifest {
  const resolve = (key: string) => `${pluginPrefix}/web/${key}`;

  return {
    routes: manifest.routes?.map((r) => ({
      path: r.path,
      componentPath: resolve(r.component),
      ...(r.config ? { config: r.config } : {}),
      ...(r.fullscreen ? { fullscreen: true } : {}),
    })),
    navItems: manifest.navItems,
    commands: manifest.commands,
    overviewCards: manifest.overviewCards,
    agentActions: manifest.agentActions?.map((a) => ({
      label: a.label,
      icon: a.icon,
      to: a.to,
      alwaysAvailable: a.alwaysAvailable,
      requiresServiceSecrets: a.requiresServiceSecrets,
      ...(a.category ? { category: a.category } : {}),
      ...(a.component ? { componentPath: resolve(a.component) } : {}),
    })),
    agentTabs: manifest.agentTabs?.map((t) => ({
      id: t.id,
      label: t.label,
      icon: t.icon,
      componentPath: resolve(t.component),
    })),
    pageTabs: manifest.pageTabs?.map((t) => ({
      slot: t.slot,
      id: t.id,
      label: t.label,
      icon: t.icon,
      componentPath: resolve(t.component),
      ...(t.order != null ? { order: t.order } : {}),
    })),
    settingsSections: manifest.settingsSections?.map((s) => ({
      id: s.id,
      label: s.label,
      icon: s.icon,
      componentPath: resolve(s.component),
      order: s.order,
    })),
    orgSettingsSections: manifest.orgSettingsSections?.map((s) => ({
      id: s.id,
      label: s.label,
      icon: s.icon,
      componentPath: resolve(s.component),
      order: s.order,
    })),
    profileSections: manifest.profileSections?.map((s) => ({
      id: s.id,
      label: s.label,
      icon: s.icon,
      componentPath: resolve(s.component),
      order: s.order,
    })),
    agentDetailSections: manifest.agentDetailSections?.map((s) => ({
      id: s.id,
      label: s.label,
      icon: s.icon,
      componentPath: resolve(s.component),
      order: s.order,
    })),
    agentHeaderBadges: manifest.agentHeaderBadges?.map((b) => ({
      id: b.id,
      componentPath: resolve(b.component),
      order: b.order,
    })),
    associationChips: manifest.associationChips?.map((c) => ({
      kind: c.kind,
      label: c.label,
      componentPath: resolve(c.component),
    })),
    homeWidgets: manifest.homeWidgets?.map((w) => ({
      id: w.id,
      componentPath: resolve(w.component),
      order: w.order,
    })),
    attachments: manifest.attachments?.map((a) => ({
      id: a.id,
      label: a.label,
      icon: a.icon,
      pickerComponentPath: resolve(a.pickerComponent),
      ...(a.order !== undefined ? { order: a.order } : {}),
      ...(a.summaryComponent ? { summaryComponentPath: resolve(a.summaryComponent) } : {}),
      ...(a.summaryIncludesIcon !== undefined
        ? { summaryIncludesIcon: a.summaryIncludesIcon }
        : {}),
    })),
    eventLabels: manifest.eventLabels,
    searchProviders: manifest.searchProviders?.map((p) => ({
      id: p.id,
      label: p.label,
      icon: p.icon,
      componentPath: resolve(p.component),
      order: p.order,
    })),
    flowTriggerRenderers: manifest.flowTriggerRenderers?.map((r) => ({
      triggerType: r.triggerType,
      label: r.label,
      icon: r.icon,
      componentPath: resolve(r.component),
    })),
    resourcePreviews: manifest.resourcePreviews?.map((p) => ({
      kind: p.kind,
      label: p.label,
      icon: p.icon,
      componentPath: resolve(p.component),
      ...(p.detailHref ? { detailHref: p.detailHref } : {}),
    })),
    marketingPreviews: manifest.marketingPreviews?.map((p) => ({
      id: p.id,
      label: p.label,
      ...(p.description ? { description: p.description } : {}),
      surface: p.surface,
      componentPath: resolve(p.component),
      ...(p.order != null ? { order: p.order } : {}),
    })),
  };
}

// NOTE: definePluginMcp is NOT exported from this browser-safe module
// because it depends on PluginMcpDefinition from types/server.js which
// transitively imports server-only packages. Use the root barrel in
// server-side plugin definitions instead.
